/**
 * Frontend client for the IRL matcher. Handles three things:
 *   1. Hashing files (filename + size + lastModified) into stable cache keys.
 *   2. localStorage cache keyed by project + IRL-list-version + file-hash.
 *   3. POSTing un-cached previews to /api/irl-match in batches.
 *
 * The cache version is the SHA-256 of the IRL list itself (number + request
 * for every item, sorted) — when the project's IRL list changes, every
 * previous match is invalidated.
 */

import { detectFormat, extractPreview } from "./extractors";
import { isCodeExcluded } from "./folderTree";
import type { FilePreview, IRLItem, MatchRequestBody, MatchResponseBody, MatchResult } from "./types";
import { apiFetch } from "../../api/fddClient";

const CACHE_KEY = "__irlMatches";
const BATCH_SIZE = 5;

type CacheBucket = {
  irlListVersion: string;
  results: Record<string, MatchResult>; // keyed by file hash
};

type CacheMap = Record<string, CacheBucket>; // keyed by projectId

function readCache(): CacheMap {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as CacheMap) : {};
  } catch {
    return {};
  }
}

function writeCache(map: CacheMap): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(map));
  } catch {
    // ignore quota errors
  }
}

async function sha256Hex(input: string): Promise<string> {
  const enc = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export async function hashFile(file: File): Promise<string> {
  return sha256Hex(`${file.name}|${file.size}|${file.lastModified}`);
}

export async function hashIrlList(list: IRLItem[]): Promise<string> {
  const canonical = [...list]
    .sort((a, b) => a.number.localeCompare(b.number))
    .map((i) => `${i.number}\t${i.request}`)
    .join("\n");
  return sha256Hex(canonical);
}

function getBucket(map: CacheMap, projectId: string, version: string): CacheBucket {
  const existing = map[projectId];
  if (existing && existing.irlListVersion === version) return existing;
  // Different version (or no entry) — start fresh for this project.
  const fresh: CacheBucket = { irlListVersion: version, results: {} };
  map[projectId] = fresh;
  return fresh;
}

export async function readCachedMatch(
  projectId: string,
  irlListVersion: string,
  fileHash: string,
): Promise<MatchResult | null> {
  const bucket = readCache()[projectId];
  if (!bucket || bucket.irlListVersion !== irlListVersion) return null;
  const cached = bucket.results[fileHash];
  if (!cached || isTransientErrorResult(cached)) return null;
  return cached;
}

function cacheMatch(
  projectId: string,
  irlListVersion: string,
  result: MatchResult,
): void {
  if (isTransientErrorResult(result)) return; // don't poison the cache
  const map = readCache();
  const bucket = getBucket(map, projectId, irlListVersion);
  bucket.results[result.hash] = result;
  writeCache(map);
}

/**
 * Reasonings that represent transient backend / network failures rather
 * than a real classification. Caching these would prevent retries — when
 * the user clicks "Run IRL match" again, we want a fresh API call, not
 * the stale "matcher unavailable" placeholder from a 504 minutes ago.
 */
const TRANSIENT_ERROR_REASONINGS = new Set([
  "matcher unavailable",
  "response unparseable",
  "no result returned",
]);

function isTransientErrorResult(result: MatchResult): boolean {
  return TRANSIENT_ERROR_REASONINGS.has(result.reasoning);
}

/**
 * Classify a list of files. Walks the cache first; only un-cached files are
 * sent to the backend. Returns results in the same order as input files.
 *
 * `folderCodes` is a parallel array — each file's full destination folder
 * code (e.g., "1.2.3"), or null for root. `excludedFolders` is the project's
 * exclusion list — codes the user unchecked in the IRL tab tree. Excluded
 * checks walk ancestors: if "1" is excluded, every "1.x.y" file is too. Files
 * in excluded folders short-circuit to a "folder excluded" result and never
 * get a preview extracted or sent to Claude.
 *
 * Throws if the network call fails entirely. Per-file errors degrade to a
 * "no match, network error" result so the UI doesn't show a spinner forever.
 */
export async function classifyFiles(args: {
  projectId: string;
  irlList: IRLItem[];
  files: File[];
  folderCodes?: (string | null)[];
  excludedFolders?: string[];
}): Promise<MatchResult[]> {
  const { projectId, irlList, files, folderCodes, excludedFolders } = args;
  if (files.length === 0) return [];
  if (irlList.length === 0) {
    return files.map((f) => emptyResult(f, "no IRL list"));
  }

  const excludedSet = new Set(excludedFolders ?? []);
  const isExcluded = (i: number): boolean => {
    if (excludedSet.size === 0) return false;
    return isCodeExcluded(folderCodes?.[i] ?? null, excludedSet);
  };

  const irlListVersion = await hashIrlList(irlList);

  // 1. Decide which files to hash. Excluded files short-circuit before any
  //    hashing, cache lookup, or extraction work.
  const out: (MatchResult | null)[] = new Array(files.length).fill(null);
  const hashes: string[] = new Array(files.length).fill("");
  for (let i = 0; i < files.length; i++) {
    if (isExcluded(i)) {
      out[i] = excludedResult(files[i]);
    } else {
      hashes[i] = await hashFile(files[i]);
    }
  }
  const cacheMap = readCache();
  const bucket = getBucket(cacheMap, projectId, irlListVersion);
  writeCache(cacheMap); // persist version bump if it changed

  const toClassify: { idx: number; file: File; hash: string }[] = [];
  for (let i = 0; i < files.length; i++) {
    if (out[i]) continue; // already excluded
    const cached = bucket.results[hashes[i]];
    if (cached && !isTransientErrorResult(cached)) {
      out[i] = cached;
    } else {
      // Either no cache hit or a stale transient error from a previous failed
      // run — re-classify so the user actually gets a fresh attempt.
      if (cached) delete bucket.results[hashes[i]];
      toClassify.push({ idx: i, file: files[i], hash: hashes[i] });
    }
  }

  if (toClassify.length === 0) {
    return out as MatchResult[];
  }

  // 2. Extract previews for the un-cached files in parallel.
  const previews: FilePreview[] = await Promise.all(
    toClassify.map(async ({ file, hash }) => {
      const format = detectFormat(file);
      let textSnippet = "";
      try {
        textSnippet = await extractPreview(file, format);
      } catch (err) {
        console.warn(`[irl-match] preview extraction failed for "${file.name}":`, err);
      }
      return {
        hash,
        fileName: file.name,
        fileSize: file.size,
        textSnippet,
        format,
      };
    }),
  );

  // 3. POST in batches.
  const batches: FilePreview[][] = [];
  for (let i = 0; i < previews.length; i += BATCH_SIZE) {
    batches.push(previews.slice(i, i + BATCH_SIZE));
  }

  const batchResults = await Promise.all(batches.map((b) => callMatchEndpoint(projectId, irlList, b)));
  const flat: MatchResult[] = batchResults.flat();
  const byHash = new Map(flat.map((r) => [r.hash, r]));

  // 4. Stitch results back into output positions, cache as we go.
  for (const { idx, hash, file } of toClassify) {
    const r = byHash.get(hash) ?? networkErrorResult(file, hash);
    out[idx] = r;
    cacheMatch(projectId, irlListVersion, r);
  }

  return out as MatchResult[];
}

async function callMatchEndpoint(
  projectId: string,
  irlList: IRLItem[],
  files: FilePreview[],
): Promise<MatchResult[]> {
  const body: MatchRequestBody = { projectId, irlList, files };
  try {
    const res = await apiFetch("/api/irl-match", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      // Throw so useIrlMatches shows an error banner — fabricating per-file
      // "matcher unavailable" results made 401/429/500 look like a silent no-op.
      const body = await res.json().catch(() => null);
      throw new Error(body?.error || `IRL match failed (HTTP ${res.status})`);
    }
    const data = (await res.json()) as MatchResponseBody;
    if (!Array.isArray(data?.results)) throw new Error("IRL match returned an unexpected response.");
    return data.results;
  } catch (err) {
    console.warn(`[irl-match] /api/irl-match failed:`, err);
    throw err instanceof Error ? err : new Error("IRL match failed — network error.");
  }
}

function emptyResult(file: File, reasoning: string): MatchResult {
  return {
    fileName: file.name,
    hash: "",
    irlNumber: null,
    confidence: 0,
    reasoning,
    alternatives: [],
  };
}

function networkErrorResult(file: { name: string }, hash: string): MatchResult {
  return {
    fileName: file.name,
    hash,
    irlNumber: null,
    confidence: 0,
    reasoning: "matcher unavailable",
    alternatives: [],
  };
}

function excludedResult(file: File): MatchResult {
  return {
    fileName: file.name,
    hash: "",
    irlNumber: null,
    confidence: 0,
    reasoning: "folder excluded",
    alternatives: [],
  };
}
